<template>
	<div class="navigator" v-if="calendarApi">
		<div class="date-scroller d-flex align-items-center" @click="backToList">
			<arrow
				direction="left"
				amount="-1"
				:class="['mr-1', {'loading-events': getIsLoadingEvents}]"
				:disabled="getIsLoadingEvents"
				:calendarApi="calendarApi"
				@updateArrowDate="updateArrowDate">
			</arrow>

			<arrow
				direction="right"
				amount="1"
				:class="{'loading-events': getIsLoadingEvents}"
				:disabled="getIsLoadingEvents"
				:calendarApi="calendarApi"
				@updateArrowDate="updateArrowDate">
			</arrow>

			<span class="d-flex align-items-center day-date-combo" v-if="getCurrentView == 'agendaDay'">
				<vue-datepicker
					ref="datepicker"
					:value="getExpandedDay"
					@input="setExpandedDay"
					class="vdp-datepicker dateRangePicker datepicker-align-left"
				></vue-datepicker>
				<span class="day-separator mx-2">|</span>
				<span class="day-of-week fs-largest">{{ dayOfWeekLabel }}</span>
			</span>

			<span class="fs-largest text-center px-2" v-if="getCurrentView != 'agendaDay'">
				{{ fullCalendarView ? fullCalendarView.title : '' }}
			</span>

			<div class="loader ml-3" v-if="getIsLoadingEvents">Loading...</div>
		</div>

		<div class="d-flex justify-content-center" style="flex-grow: 1" v-if="calendarApi">
			<div class="btn-group d-flex">
				<button
					type="button"
					class="btn"
					v-for="period in periods"
					:key="period.id"
					:class=" {
						'bg-dark lightest':
							period.value == getCurrentView &&
							!(period.value == 'agendaDay' && highlightTodayButton),
						'bg-faded dark':
							period.value != getCurrentView ||
							(period.value == 'agendaDay' && highlightTodayButton),
					}"
					@click="changeView(period)">
					{{ period.label }}
				</button>
				<button
					type="button"
					class="btn"
					:class="{
						'bg-dark lightest': highlightTodayButton,
						'bg-faded dark': !highlightTodayButton,
					}"
					@click="goToToday">
					Today
				</button>
			</div>
		</div>

		<div class="" v-if="accessPermissionFilter(['admin', 'supervisor'])">
			<button class="btn ml-4 bg-dark" @click="openCreateEvent">
				<i class="ico ico-add mt-1"></i>New event
			</button>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"

import Arrow from "./Arrow"
import VueDatepicker from '@/tools/DatePicker'

const VIEW_NAME_MAP = {
	month: 'dayGridMonth',
	agendaWeek: 'timeGridWeek',
	agendaDay: 'timeGridDay',
};

export default {
	props: {
		rangeFrom: Date,
		rangeTo: Date,
		defaultView: String,
		calendarApi: {
			type: Object,
			default: null,
		},
		fullCalendarView: {
			type: Object,
			default: null,
		},
	},
	data() {
		return {
			periods: [
				{ label: "Month", value: "month", id: 2 },
				{ label: "Week", value: "agendaWeek", id: 1 },
				{ label: "Day", value: "agendaDay", id: 0 },
			],
			calendarViewValue: 0,
			isUpdatingSupplierSettings: false,
		};
	},
	async mounted() {
		this.calendarViewValue = await this.getUserDetails.userAccountTypeDefault.defaultCalendarView;

		const defaultCalendarView = this.periods.find(
			(period) => period.id === this.calendarViewValue
		);

		this.$nextTick(() => {
			const queryParams = this.$route.params;

			this.changeView(defaultCalendarView);
			if (queryParams.expandedDay) this.updateDatePickerInfo(queryParams.expandedDay);
		});
	},
	watch: {
		getSavedDefaultCalendarViewValue(newValue) {
			this.isUpdatingSupplierSettings = newValue;

			const defaultCalendarView = this.periods.find((period) => period.id === newValue);
			this.changeView(defaultCalendarView);
		},
	},
	computed: {
		...mapGetters([
			"getExpandedDay",
			"getCurrentView",
			"getIsLoadingEvents",
			"getUserDetails",
			"getSavedDefaultCalendarViewValue",
		]),
		highlightTodayButton() {
			return (this.getCurrentView == "agendaDay" && moment(this.getExpandedDay).format("DD-MM-YYYY") == moment().format("DD-MM-YYYY"));
		},
		dayOfWeekLabel() {
			return moment(this.getExpandedDay).format("dddd");
		},
	},
	methods: {
		...mapActions([
			"updateSidebar",
			"openModal",
			"setAsCreatingSingleEvent",
			"calculateSunTimes"
		]),
		updateDatePickerInfo(date) {
			const datepicker = this.$refs.datepicker;
			if (!datepicker) return; // Element doesn't exist in DOM outside Day view (v-if)

			const [year, month, day] = date.split("-").map(Number);

			Object.assign(datepicker, {
				date: day,
				month: month - 1,
				tmpMonth: month - 1,
				year: year,
			});
		},
		setExpandedDay(date, isFromArrowsOrToday) {
			var newDate = this.formatDate(date);

			if (isFromArrowsOrToday) this.updateDatePickerInfo(newDate);

			let viewToURL = "";
			switch (this.getCurrentView) {
				case "agendaDay":
					viewToURL = "day";
					break;
				case "agendaWeek":
					viewToURL = "week";
					break;
				default:
					viewToURL = this.getCurrentView;
			}

			this.$router.push({
				name: "calendarExpanded",
				params: {
					currentView: viewToURL,
					expandedDay: newDate,
				},
			});

			this.updateSidebar(newDate);
			this.calculateSunTimes();

			if (this.calendarApi) {
				this.calendarApi.gotoDate(newDate);
			}
		},
		updateArrowDate(dates) {
			this.$emit("updateArrowDate", dates);
			this.setExpandedDay(dates, true);
		},
		updateRangeTo(to) {
			this.$emit("updateRanges", { from: null, to: to });
		},
		backToList() {
			const eventsPanel = document.querySelector('.events');
			if (!eventsPanel) return;

			const listEl = eventsPanel.querySelector('.list');
			const moreEl = eventsPanel.querySelector('.more');
			if (listEl) listEl.classList.remove('moved-left');
			if (moreEl) moreEl.classList.add('moved-right');
		},
		changeView(period) {
			if (!this.calendarApi) return;

			this.calendarApi.changeView(VIEW_NAME_MAP[period.value] || period.value);

			let viewNameForUrl = "";

			switch (period.value) {
				case "agendaDay":
					viewNameForUrl = "day";
					this.calendarApi.gotoDate(this.getExpandedDay);
					break;
				case "agendaWeek":
					viewNameForUrl = "week";
					break;
				case "month":
					viewNameForUrl = "month";
					break;
				default:
			}

			const routeNameMap = {
				calendarExpandedWithEventIdEdit: 'calendarExpandedWithEventIdEdit',
				calendarExpandedWithEventId: 'calendarExpandedWithEventId',
			};
			const targetName = routeNameMap[this.$router.history.current.name] || 'calendarExpanded';
			this.$router.push({
				name: targetName,
				params: targetName === 'calendarExpanded'
					? { currentView: viewNameForUrl, expandedDay: this.getExpandedDay }
					: { currentView: viewNameForUrl, expandedDay: this.getExpandedDay, eventID: this.$route.params.eventID },
			});
		},
		goToToday() {
			this.setExpandedDay(moment(), true);
			if (this.calendarApi) {
				this.calendarApi.changeView('timeGridDay');
			}
			this.backToList();
		},
		openCreateEvent() {
			this.setAsCreatingSingleEvent();
			this.openModal("EventEdit");
		},
	},
	components: {
		Arrow,
		VueDatepicker,
	},
};
</script>

<style lang="scss">
	@import "../../assets/scss/_variables.scss";
	@import "../../assets/scss/_utility.scss";

	.navigator {
		border-bottom: 1px solid $gray-light;

		button.bg-dark:hover,
		button.bg-dark:focus {
			background-color: $dark !important;
		}

		.main-column {
			overflow: visible;
		}

		.date-scroller {
			.vdp-datepicker .input {
				text-align: center;
				margin: 0;
				font-size: $font-size-largest;
				background: transparent;
				border: 0;
				padding: 0;
				width: 160px;
			}
			
			.day-date-combo .vdp-datepicker .input {
				text-align: left;
				width: auto;
				padding-left: 0.35rem;
			}

			.arrow {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 25px;
				height: 25px;
				border-radius: 50%;
				background-color: $gray-dark;

				color: $lightest;
				text-align: center;
			}

			.arrow.loading-events {
				background-color: $medium;
			}
		}
	}

	.loaderHolder {
		background: #c5d0de;
		padding: 7px;
		width: 52px;
		@extend .radius-normal;
	}

	.loader,
	.loader:before,
	.loader:after {
		border-radius: 50%;
		width: 1em;
		height: 1em;
		-webkit-animation-fill-mode: both;
		animation-fill-mode: both;
		-webkit-animation: load7 1.8s infinite ease-in-out;
		animation: load7 1.8s infinite ease-in-out;
	}

	.loader {
		color: #8ca0b3;
		font-size: 10px;
		position: relative;
		text-indent: -9999em;
		-webkit-transform: translateZ(0);
		-ms-transform: translateZ(0);
		transform: translateZ(0);
		-webkit-animation-delay: -0.16s;
		animation-delay: -0.16s;
	}

	.loader:before,
	.loader:after {
		content: "";
		position: absolute;
		top: 0;
	}

	.loader:before {
		left: -1.3em;
		-webkit-animation-delay: -0.32s;
		animation-delay: -0.32s;
	}

	.loader:after {
		left: 1.3em;
	}

	@-webkit-keyframes load7 {
		0%, 80%, 100% { box-shadow: 15px 0 0 -1.3em; }
		40% { box-shadow: 15px 0 0 0; }
	}

	@keyframes load7 {
		0%, 80%, 100% { box-shadow: 15px 0 0 -1.3em; }
		40% { box-shadow: 15px 0 0 0; }
	}
</style>


// WEBPACK FOOTER //
// Navigator.vue?274626f3